import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Permissions from '../auth/Permissions';
import Button from '../core/Button';
import Input from '../core/Input';
import { DialogWindow } from '../DialogWindow';
import {
  reviveErrorReport,
  useErrorReportsMutations,
} from '../../hooks/useErrorReports';
import useSchemaManagementDialog from '../../store/schemaManagementDialog';
import { IconPlus } from '../../icons';
import { Dropdown, OptionsType } from '../core/Dropdown';

const messages = defineMessages({
  choose: { id: 'choose', defaultMessage: 'Choose...' },
  choose_property: {
    id: 'error_report.choose_property',
    defaultMessage: 'Choose property',
  },
  property: {
    id: 'error_report.property',
    defaultMessage: 'Property',
  },
  value_optional: {
    id: 'error_report.value_optional',
    defaultMessage: 'Value (optional)',
  },
  choose_a_stop: {
    id: 'error_report.choose_a_stop',
    defaultMessage: 'Choose a stop',
  },
  error_description: {
    id: 'error_report.error_description',
    defaultMessage: 'Error description',
  },
  enter_error_description: {
    id: 'error_report.enter_error_description',
    defaultMessage: 'Enter error description...',
  },
  external_support: {
    id: 'error_report.external_support',
    defaultMessage: 'External support',
  },
  connection_error: {
    id: 'error_report.connection_error',
    defaultMessage: 'Connection error',
  },
});

interface NewErrorFormProps {
  availableProps:
    | {
        id: string;
        name: string;
      }[]
    | undefined;
  selectableEntities: Entity[];
  schema: EntitySchema;
  setSelectedEntityId: (id: string | null) => void;
  selectedEntityId: string | null;
  onCancel: () => void;
  onCreated: (report: ErrorReport) => void;
  onDirty: (dirty: boolean) => void;
  entityName: string;
}

const createErrorMailTo = (
  destination: string,
  propName: string,
  entityName: string,
  message?: string,
): string => {
  const subject = encodeURIComponent(`${entityName}: ${propName}`);
  let link = `mailto:${destination}?subject=${subject}`;
  if (message) {
    link += '&body=' + encodeURIComponent(message);
  }
  return link;
};

const NewErrorForm: React.FC<NewErrorFormProps> = ({
  availableProps,
  selectableEntities,
  schema,
  setSelectedEntityId,
  selectedEntityId,
  onCancel,
  onCreated,
  onDirty,
  entityName,
}) => {
  const {
    create: {
      mutateAsync: createErrorReport,
      isLoading: creatingErrorReportInProgress,
    },
  } = useErrorReportsMutations();
  const isMounted = useRef(false);
  const createdErrorReportRef = useRef<ErrorReport | undefined>(undefined);
  const intl = useIntl();
  const { show: showSchemaManagement, setShow: setShowSchemaManagement } =
    useSchemaManagementDialog();

  const [message, setMessage] = useState('');
  const [showLink, setShowLink] = useState<
    { link: string; label: string } | undefined
  >(undefined);
  const [selectedProp, setSelectedProp] = useState<{
    id: string;
    name: string;
  } | null>(() => availableProps?.[0] ?? null);
  const [showError, setShowError] = useState<boolean>(false);
  const [selectedPropValueId, setSelectedPropValueId] = useState<string>();
  const initialEntityIdRef = useRef(selectedEntityId);
  const initialPropIdRef = useRef(availableProps?.[0]?.id ?? null);

  const isDirty = useMemo(() => {
    if (!availableProps) {
      return false;
    }
    return (
      message !== '' ||
      (selectedProp?.id ?? null) !== initialPropIdRef.current ||
      selectedPropValueId !== undefined ||
      selectedEntityId !== initialEntityIdRef.current
    );
  }, [
    availableProps,
    message,
    selectedEntityId,
    selectedProp,
    selectedPropValueId,
  ]);

  useEffect(() => {
    onDirty(isDirty);
  }, [isDirty, onDirty]);

  const propOptions = useMemo<OptionsType[]>(() => {
    return (
      availableProps?.map((p) => ({
        id: p.id,
        label: p.name,
      })) || []
    );
  }, [availableProps]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onMessageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setMessage(e.target.value || '');
    },
    [setMessage],
  );

  const availableEnumOptions = useMemo<OptionsType[] | undefined>(() => {
    if (!selectedProp) {
      return undefined;
    }
    const prop = schema.definition.propertiesLookup[selectedProp.id];
    if (!prop || prop.type !== 'enum' || !prop.alternatives) {
      return undefined;
    }
    const extras = schema.extras[prop.key];
    const { support_urls } = extras;
    if (!support_urls) {
      return undefined;
    }
    return prop.alternatives
      .filter((a) => !!support_urls[a])
      .map((alternative) => ({
        id: alternative,
        label: alternative,
      }));
  }, [selectedProp, schema]);

  const onLinkOk = useCallback(() => {
    const created = createdErrorReportRef.current;
    createdErrorReportRef.current = undefined;
    if (created) {
      onCreated(created);
      return;
    }
    setShowLink(undefined);
    setMessage('');
    setSelectedEntityId(null);
    setSelectedProp(null);
  }, [onCreated, setSelectedEntityId]);

  const onErrorConfirm = useCallback(() => {
    setMessage('');
    setShowError(false);
  }, [setShowError]);

  const onSave = useCallback(() => {
    if (!selectedEntityId || !selectedProp) {
      return;
    }
    const { support_urls } = schema.extras[selectedProp.id];
    if (!support_urls) {
      return;
    }
    const destination =
      (selectedPropValueId && support_urls[selectedPropValueId]) ||
      support_urls.default;
    createErrorReport({
      message: message.length > 0 ? message : undefined,
      entity_id: selectedEntityId,
      destination: destination,
      property_key: selectedProp.id,
      property_value: selectedPropValueId,
    })
      .catch((err) => {
        const status = err.status as number | undefined;
        if (!status || (isFinite(status) && status > 404)) {
          if (isMounted.current) {
            setShowError(true);
            return true;
          } else {
            throw err;
          }
        }
      })
      .then((errorDetected) => {
        if (typeof errorDetected === 'object' && isMounted.current) {
          createdErrorReportRef.current = reviveErrorReport(errorDetected);
          const link = destination.startsWith('http')
            ? destination
            : createErrorMailTo(
                destination,
                selectedProp.name,
                entityName,
                message,
              );
          setShowLink({ link: link, label: destination });
        }
      })
      .catch((err) => {
        throw err;
      });
  }, [
    createErrorReport,
    entityName,
    message,
    schema.extras,
    selectedEntityId,
    selectedProp,
    selectedPropValueId,
  ]);

  if (!availableProps) {
    return (
      <>
        <p className="mt-3 text-sm leading-6">
          <FormattedMessage
            id="error_report.no_keys_available"
            defaultMessage="No contact information specified for reporting faults and errors."
          />
        </p>
        <Permissions has="entities:create">
          <p className="mb-1 text-sm leading-6">
            <FormattedMessage
              id="error_report.click_to_view_contact_info_in_schema_manager"
              defaultMessage="Click below to view schema manager and add contact information for error reporting."
            />
          </p>
          <Button
            variant="filled"
            onClick={() => setShowSchemaManagement(!showSchemaManagement)}
          >
            <FormattedMessage
              id="error_report.go_to_schema_manager"
              defaultMessage="Go to schema manager"
            />
          </Button>
        </Permissions>
        <Permissions noneOf={['entities:create']}>
          <p className="mb-1 mt-3 text-sm leading-6">
            <FormattedMessage
              id="error_report.contact_admin_to_add_error_reporting_contact_information"
              defaultMessage="Contact admin to add error reporting contact information."
            />
          </p>
        </Permissions>
      </>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <DialogWindow
        open={!!showLink}
        title={intl.formatMessage(messages.external_support)}
        content={
          <>
            <p className="mt-3 text-sm leading-6">
              <FormattedMessage
                id="error_report.file_report_with_contact_external_support"
                defaultMessage="Please use the link below to file a report with external support."
              />
            </p>
            <a href={showLink?.link} target="_blank" rel="noreferrer">
              <span className="text-base leading-6">{showLink?.label}</span>
            </a>
          </>
        }
        footer={
          <Button variant="filled" onClick={onLinkOk}>
            <FormattedMessage id="ok" defaultMessage="Ok" />
          </Button>
        }
      />
      <DialogWindow
        open={showError}
        title={intl.formatMessage(messages.connection_error)}
        content={
          <>
            <p className="mt-3 text-sm leading-6">
              <FormattedMessage
                id="error_report.could_not_create_error_report"
                defaultMessage="Something went wrong, please check your connection and try again.{br}
              The external report link will only be shown after successfully creating a report in Mobilix.{br}
              If the problem persists, please contact support@allbinary.se."
                values={{ br: <br /> }}
              />
            </p>
            <a href={showLink?.link} target="_blank" rel="noreferrer">
              <span className="text-base leading-6">{showLink?.label}</span>
            </a>
          </>
        }
        footer={
          <Button variant="filled" onClick={onErrorConfirm}>
            <FormattedMessage id="ok" defaultMessage="Ok" />
          </Button>
        }
      />
      <div className="flex grow flex-col gap-8">
        <div>
          <h3 className="mb-2 text-lg font-bold">
            <FormattedMessage
              id="error_report.new_error_report"
              defaultMessage="New error report"
            />
          </h3>
          <p className="mb-1 text-base font-medium leading-6 text-text-900">
            {intl.formatMessage(messages.choose_a_stop)}
          </p>
          <div className="flex flex-row flex-wrap gap-1">
            {selectableEntities.map((e) => (
              <Button
                key={e.id}
                onClick={() => setSelectedEntityId(e.id)}
                className="size-10 rounded-full"
                variant={e.id === selectedEntityId ? 'filled' : 'outline'}
              >
                {e.stop_letter}
              </Button>
            ))}
          </div>
        </div>
        <Dropdown
          id="error-report-property"
          label={intl.formatMessage(messages.property)}
          placeholder={intl.formatMessage(messages.choose_property)}
          options={propOptions}
          value={
            selectedProp
              ? { id: selectedProp.id, label: selectedProp.name }
              : undefined
          }
          onValueChange={(o) => {
            setSelectedProp(availableProps.find((p) => p.id === o?.id) || null);
            setSelectedPropValueId(undefined);
          }}
          disabled={!selectedEntityId}
          preventReselect
        />
        {availableEnumOptions && availableEnumOptions.length > 0 && (
          <Dropdown
            id="error-report-property-value"
            label={intl.formatMessage(messages.value_optional)}
            placeholder={intl.formatMessage(messages.choose)}
            options={availableEnumOptions}
            value={
              selectedPropValueId
                ? { id: selectedPropValueId, label: selectedPropValueId }
                : undefined
            }
            onValueChange={(o) => setSelectedPropValueId(o?.id)}
            disabled={!selectedEntityId}
          />
        )}
        <Input
          id="error-report-description"
          type="multiline"
          label={intl.formatMessage(messages.error_description)}
          disabled={!selectedEntityId}
          rows={4}
          placeholder={intl.formatMessage(messages.enter_error_description)}
          value={message}
          onChange={onMessageChange}
        />
      </div>
      <div className="mt-auto flex justify-between gap-2 pt-6">
        <Button
          disabled={creatingErrorReportInProgress}
          onClick={onCancel}
          variant="ghost"
          color="red"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          loading={creatingErrorReportInProgress}
          startIcon={<IconPlus />}
          disabled={!selectedProp || !selectedEntityId || !message}
          onClick={onSave}
          variant="filled"
        >
          <FormattedMessage
            id="error_report.create"
            defaultMessage="Create error report"
          />
        </Button>
      </div>
    </div>
  );
};

export default NewErrorForm;
